import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { Icon } from '@sb/design-system';

import styles from './ToolRunTicker.module.css';

const ICON_COLOR = 'var(--label-tertiary)';

/** Matches the roll-in duration in the CSS module. */
const ROLL_DURATION_MS = 200;

/**
 * Minimum time between rolls: the animation itself plus a settle so
 * back-to-back ticks read as distinct movements instead of a blur.
 */
const ROLL_COOLDOWN_MS = ROLL_DURATION_MS + 150;

/**
 * The ticking number: on a tick the old digit disappears outright and
 * the new one rolls in from below (keyed on the value so the entrance
 * replays every increment).
 *
 * Rolls are rate-limited: while one is playing, further `value`
 * changes are held back, and when the cooldown ends the display rolls
 * once directly to the latest value (2 → 5 in a single movement) —
 * rapid tool bursts don't turn the counter into a slot machine.
 *
 * `digitClassName` is applied to the digit span directly: an
 * ancestor's `background-clip: text` doesn't paint through the
 * transformed digit element, so the shimmer must own a background on
 * the leaf span itself.
 */
function AnimatedCount({
  value,
  digitClassName,
}: {
  value: number;
  digitClassName?: string;
}) {
  const [current, setCurrent] = useState(value);

  // Bumped when a cooldown ends so the effect below re-runs and rolls
  // to wherever `value` has moved in the meantime.
  const [rollGeneration, setRollGeneration] = useState(0);

  // Non-null while a roll is playing out its cooldown.
  const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (cooldownRef.current !== null) clearTimeout(cooldownRef.current);
    },
    [],
  );

  useEffect(() => {
    if (value === current) return;

    // A roll is still playing — hold until its cooldown fires.
    if (cooldownRef.current !== null) return;

    setCurrent(value);

    cooldownRef.current = setTimeout(() => {
      cooldownRef.current = null;
      setRollGeneration((generation) => generation + 1);
    }, ROLL_COOLDOWN_MS);
  }, [value, current, rollGeneration]);

  return (
    <span className={styles.countWindow}>
      <span
        key={`in-${current}`}
        className={cx(styles.count, styles.rollIn, digitClassName)}
      >
        {current}
      </span>
    </span>
  );
}

interface ToolRunTickerProps {
  /** How many tools have run (or started running) so far. */
  count: number;
  /** True once every tool in the group has finished. */
  isDone: boolean;
  /** True when at least one tool errored — swaps the checkmark for an x. */
  hasError?: boolean;
  /** When provided, the line is clickable and expands to the full list. */
  onExpand?: () => void;
}

/**
 * Single-line tool-run summary: `{x} tools ran`, ticking up as the
 * agent calls more tools during one response — replaces the old
 * line-per-tool stream. While tools run there is no spinner icon; the
 * text itself shimmers (Codex-style) and the count rolls like an
 * odometer. When the batch finishes the shimmer stops and a checkmark
 * (or an x, on error) takes the icon slot.
 */
export function ToolRunTicker({
  count,
  isDone,
  hasError = false,
  onExpand,
}: ToolRunTickerProps) {
  const isShimmering = !isDone;

  // The animated wrapper owns the slot's width so the checkmark can
  // grow the slot open (see done-icon-in) instead of shoving the text
  // sideways the frame it mounts.
  const statusIcon = isDone ? (
    <span
      className={cx(
        styles.doneIconIn,
        'tw-flex',
        'tw-shrink-0',
        'tw-items-center',
        'tw-justify-center',
      )}
      style={{ height: 14 }}
    >
      <Icon
        kind={hasError ? 'xmark' : 'checkmark'}
        data-testid="chat-tool-ticker-status-icon"
        className="tw-shrink-0"
        style={{ width: 14, height: 14, color: ICON_COLOR }}
      />
    </span>
  ) : null;

  // Number and words are separate flex items (a plain leading space
  // in the words' text node would collapse away), so the word gap is
  // a small flex gap on the label wrapper below.
  const label = (
    <>
      <AnimatedCount
        value={count}
        digitClassName={isShimmering ? styles.shimmerText : undefined}
      />
      <span>{count === 1 ? 'tool ran' : 'tools ran'}</span>
    </>
  );

  const labelClasses = cx(
    'tw-flex',
    'tw-items-center',
    'tw-gap-4',
    isShimmering && styles.shimmerText,
  );

  const rowClasses = cx('tw-flex', 'tw-items-center', 'tw-gap-8', 'tw-text-13');

  if (onExpand) {
    return (
      <button
        type="button"
        data-testid="chat-tool-ticker"
        onClick={onExpand}
        className={cx(
          rowClasses,
          'tw-bg-transparent',
          'tw-border-none',
          'tw-p-0',
          'tw-m-0',
          'tw-cursor-pointer',
          'tw-text-left',
        )}
        style={{ fontFamily: 'inherit', color: 'var(--label-secondary)' }}
      >
        {statusIcon}
        <span className={labelClasses}>{label}</span>
        <span className="tw-ml-2" style={{ color: 'var(--label-quaternary)' }}>
          ▸
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid="chat-tool-ticker"
      className={rowClasses}
      style={{ color: 'var(--label-secondary)' }}
    >
      {statusIcon}
      <span className={labelClasses}>{label}</span>
    </div>
  );
}
